import { useEffect, useRef, useState } from "react";
import { Crosshair, Loader2, MapPin, Search, Send } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { linkDoGoogleMaps, quadradosDoMapa } from "../../utils/localizacao";

// Enviar localização para o cliente.
//
// A conversa já recebia mapa e não sabia mandar nenhum: para combinar um
// ponto de encontro, a saída era colar um link do Google Maps no meio do
// texto. Aqui sai como localização de verdade — o cartão que o WhatsApp abre
// no Waze ou no Maps com um toque.
//
const EnviarLocalizacaoModal = ({ aberto, aoFechar, ticketId }) => {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState([]);
  const [semResultados, setSemResultados] = useState(false);
  const [modo, setModo] = useState(null);
  const [pesquisando, setPesquisando] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [centro, setCentro] = useState({ lat: -15.793889, lon: -47.882778 });
  const [marcador, setMarcador] = useState({ x: 0, y: 0 });
  const arraste = useRef(null);
  const [arrastando, setArrastando] = useState(false);
  const ignorarClique = useRef(false);

  const lat = Number(String(latitude).replace(",", "."));
  const lon = Number(String(longitude).replace(",", "."));
  const valido =
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    String(latitude).trim() !== "" &&
    String(longitude).trim() !== "" &&
    Math.abs(lat) <= 90 &&
    Math.abs(lon) <= 180;

  const usarMinhaLocalizacao = () => {
    setModo("atual");
    if (!navigator.geolocation) {
      toast.error("Este navegador não sabe informar a localização.");
      return;
    }

    setBuscando(true);
    navigator.geolocation.getCurrentPosition(
      (posicao) => {
        setLatitude(String(posicao.coords.latitude));
        setLongitude(String(posicao.coords.longitude));
        setBuscando(false);
      },
      (erro) => {
        setBuscando(false);
        // O navegador só entrega a localização em https (ou em localhost) e
        // com permissão: dizer qual dos dois faltou poupa a investigação.
        toast.error(
          erro?.code === 1
            ? "Permissão de localização negada no navegador."
            : "Não foi possível obter a localização agora.",
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const pesquisarEndereco = async (termo = busca) => {
    if (termo.trim().length < 3) return;

    setPesquisando(true);
    setSemResultados(false);
    try {
      const resposta = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&countrycodes=br&accept-language=pt-BR&q=${encodeURIComponent(termo.trim())}`,
      );
      if (!resposta.ok) throw new Error("Falha na busca");
      let encontrados = await resposta.json();

      // Endereços são bem atendidos pelo Nominatim, mas nomes comerciais e
      // pontos de referência às vezes só aparecem no índice do Photon.
      if (encontrados.length === 0) {
        const alternativa = await fetch(
          `https://photon.komoot.io/api/?limit=10&q=${encodeURIComponent(termo.trim())}`,
        );
        if (!alternativa.ok) throw new Error("Falha na busca alternativa");
        const dados = await alternativa.json();
        const normalizar = (texto) =>
          String(texto || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();
        const termos = normalizar(termo)
          .split(/\s+/)
          .filter((item) => item.length > 2);
        const genericos = new Set([
          "supermercado",
          "distribuidora",
          "mercado",
          "loja",
          "empresa",
        ]);

        encontrados = (dados.features || [])
          .filter((item) =>
            ["br", "bra", "brasil", "brazil"].includes(
              String(
                item.properties?.countrycode || item.properties?.country || "",
              ).toLowerCase(),
            ),
          )
          .map((item) => {
            const propriedades = item.properties || {};
            const [lonResultado, latResultado] = item.geometry.coordinates;
            return {
              place_id: `photon-${propriedades.osm_type}-${propriedades.osm_id}`,
              lat: latResultado,
              lon: lonResultado,
              name: propriedades.name,
              display_name: [
                propriedades.name,
                propriedades.street,
                propriedades.district,
                propriedades.city,
                propriedades.state,
              ]
                .filter(Boolean)
                .filter(
                  (valor, indice, lista) => lista.indexOf(valor) === indice,
                )
                .join(", "),
              relevancia: termos.reduce(
                (total, palavra) =>
                  total +
                  (normalizar(propriedades.name).includes(palavra)
                    ? genericos.has(palavra)
                      ? 1
                      : 4
                    : 0),
                0,
              ),
            };
          })
          .sort((a, b) => b.relevancia - a.relevancia)
          .slice(0, 5);
      }
      setResultados(encontrados);
      setSemResultados(encontrados.length === 0);

    } catch {
      toast.error("Não foi possível buscar o endereço agora.");
    } finally {
      setPesquisando(false);
    }
  };

  useEffect(() => {
    if (modo !== "mapa" || busca.trim().length < 3) {
      setSemResultados(false);
      return undefined;
    }

    const temporizador = window.setTimeout(() => pesquisarEndereco(busca), 700);
    return () => window.clearTimeout(temporizador);
    // A busca precisa reagir somente ao texto e ao modo escolhido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, modo]);

  const selecionarResultado = (resultado) => {
    setCentro({ lat: Number(resultado.lat), lon: Number(resultado.lon) });
    setMarcador({ x: 0, y: 0 });
    setLatitude(String(resultado.lat));
    setLongitude(String(resultado.lon));
    setNome(resultado.name || "");
    setEndereco(resultado.display_name || busca);
    setResultados([]);
  };

  const deslocarCentro = (origem, dx, dy) => {
    const centroLat = origem.lat;
    const centroLon = origem.lon;
    const zoom = 16;
    const n = 2 ** zoom;
    const limite = (valor, minimo, maximo) =>
      Math.min(maximo, Math.max(minimo, valor));
    const latLimitada = limite(centroLat, -85.05112878, 85.05112878);
    const radianos = (latLimitada * Math.PI) / 180;
    const centroX = ((centroLon + 180) / 360) * n;
    const centroY =
      ((1 - Math.log(Math.tan(radianos) + 1 / Math.cos(radianos)) / Math.PI) /
        2) *
      n;
    const x = centroX + dx / 256;
    const y = limite(centroY + dy / 256, 0, n);
    const novaLongitude = (x / n) * 360 - 180;
    const novaLatitude =
      (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;
    return { lat: novaLatitude, lon: ((novaLongitude + 180) % 360 + 360) % 360 - 180 };
  };

  const marcarNoMapa = (evento) => {
    if (ignorarClique.current) {
      ignorarClique.current = false;
      return;
    }
    const area = evento.currentTarget.getBoundingClientRect();
    const dx = evento.detail === 0 ? 0 : evento.clientX - area.left - area.width / 2;
    const dy = evento.detail === 0 ? 0 : evento.clientY - area.top - area.height / 2;
    const ponto = deslocarCentro(centro, dx, dy);

    setLatitude(String(ponto.lat));
    setLongitude(String(ponto.lon));
    setMarcador({ x: dx, y: dy });
    setEndereco("");
    setNome("");
    setResultados([]);
  };

  const enviar = async () => {
    if (!valido) return;

    setEnviando(true);

    try {
      await api.post(`/messages/${ticketId}/location`, {
        latitude: lat,
        longitude: lon,
        nome: nome.trim() || undefined,
        endereco: endereco.trim() || undefined,
      });

      toast.success("Localização enviada.");
      aoFechar();
    } catch (err) {
      toastError(err);
    }

    setEnviando(false);
  };

  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && aoFechar()}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-lg min-w-0 overflow-x-hidden overflow-y-auto rounded-xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-red-500" />
            Enviar localização
          </DialogTitle>
          <DialogDescription>
            Procure um endereço ou toque no mapa para marcar o local.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={usarMinhaLocalizacao}
              disabled={buscando || enviando}
              className={`flex min-h-28 flex-col items-center justify-center gap-2 rounded-xl border p-4 text-center transition-colors hover:bg-accent disabled:opacity-50 ${
                modo === "atual"
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "bg-card"
              }`}
            >
              {buscando ? (
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
              ) : (
                <Crosshair className="h-7 w-7 text-primary" />
              )}
              <span className="text-sm font-medium">Localização atual</span>
              <span className="text-xs text-muted-foreground">
                Usa o local deste dispositivo
              </span>
            </button>
            <button
              type="button"
              onClick={() => setModo("mapa")}
              disabled={enviando}
              className={`flex min-h-28 flex-col items-center justify-center gap-2 rounded-xl border p-4 text-center transition-colors hover:bg-accent disabled:opacity-50 ${
                modo === "mapa"
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "bg-card"
              }`}
            >
              <MapPin className="h-7 w-7 text-primary" />
              <span className="text-sm font-medium">Marcar no mapa</span>
              <span className="text-xs text-muted-foreground">
                Busque ou escolha um ponto
              </span>
            </button>
          </div>

          {modo === "atual" && valido && (
            <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
              <MapPin className="h-4 w-4 text-primary" />
              Localização atual pronta para enviar.
            </div>
          )}

          {modo === "mapa" && (
            <>
              <div className="relative space-y-1">
                <Label htmlFor="local-endereco-busca">Digite o endereço</Label>
                <div className="flex min-w-0 gap-2">
                  <Input
                    id="local-endereco-busca"
                    className="min-w-0 flex-1"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        pesquisarEndereco();
                      }
                    }}
                    placeholder="Rua, número, bairro ou cidade"
                    disabled={enviando}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Buscar endereço"
                    className="shrink-0"
                    onClick={() => pesquisarEndereco()}
                    disabled={pesquisando || busca.trim().length < 3}
                  >
                    {pesquisando ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {resultados.length > 0 && (
                  <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-lg">
                    {resultados.map((resultado) => (
                      <button
                        type="button"
                        key={`${resultado.place_id}-${resultado.lat}-${resultado.lon}`}
                        onClick={() => selecionarResultado(resultado)}
                        className="block w-full whitespace-normal break-words rounded-sm px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                      >
                        {resultado.display_name}
                      </button>
                    ))}
                  </div>
                )}
                {semResultados && !pesquisando && (
                  <p className="text-xs text-muted-foreground">
                    Endereço não encontrado. Tente informar rua, número e
                    cidade.
                  </p>
                )}
              </div>

              <div className="min-w-0 overflow-hidden rounded-xl border">
                <button
                  type="button"
                  onClick={marcarNoMapa}
                  onPointerDown={(evento) => {
                    if (evento.button !== 0 || !evento.isPrimary) return;
                    ignorarClique.current = false;
                    arraste.current = { id: evento.pointerId, x: evento.clientX, y: evento.clientY, centro, marcador };
                    evento.currentTarget.setPointerCapture(evento.pointerId);
                  }}
                  onPointerMove={(evento) => {
                    const inicio = arraste.current;
                    if (!inicio || inicio.id !== evento.pointerId) return;
                    const dx = evento.clientX - inicio.x;
                    const dy = evento.clientY - inicio.y;
                    if (!ignorarClique.current && Math.hypot(dx, dy) < 5) return;
                    ignorarClique.current = true;
                    setArrastando(true);
                    setCentro(deslocarCentro(inicio.centro, -dx, -dy));
                    setMarcador({ x: inicio.marcador.x + dx, y: inicio.marcador.y + dy });
                  }}
                  onLostPointerCapture={() => {
                    arraste.current = null;
                    setArrastando(false);
                  }}
                  className={`relative block h-56 w-full touch-none select-none overflow-hidden bg-muted ${arrastando ? "cursor-grabbing" : "cursor-grab"}`}
                  aria-label="Mapa: clique para marcar a localização"
                >
                  {quadradosDoMapa(
                    centro.lat,
                    centro.lon,
                    {
                      largura: 500,
                      altura: 224,
                    },
                  ).map((quadrado) => (
                    <img
                      key={quadrado.chave}
                      src={quadrado.url}
                      alt=""
                      draggable="false"
                      className="pointer-events-none absolute h-64 w-64 max-w-none select-none"
                      style={{ left: `calc(50% + ${quadrado.left - 250}px)`, top: quadrado.top }}
                    />
                  ))}
                  {valido && (
                    <MapPin
                      className={`pointer-events-none absolute h-8 w-8 -translate-x-1/2 -translate-y-full fill-red-500 text-white drop-shadow ${arrastando ? "" : "transition-[left,top] duration-300 ease-out motion-reduce:transition-none"}`}
                      style={{ left: `calc(50% + ${marcador.x}px)`, top: `calc(50% + ${marcador.y}px)` }}
                    />
                  )}
                  {!valido && (
                    <span className="absolute inset-x-4 bottom-4 rounded-md bg-background/90 px-3 py-2 text-sm shadow">
                      Arraste para explorar. Toque para marcar o local.
                    </span>
                  )}
                  <span className="absolute bottom-0 right-0 bg-background/80 px-1 text-[9px] text-muted-foreground">
                    © OpenStreetMap
                  </span>
                </button>
                {valido && (
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {endereco || "Local marcado no mapa"}
                    </span>
                    <a
                      href={linkDoGoogleMaps(lat, lon)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-xs text-primary hover:underline"
                    >
                      Conferir no mapa
                    </a>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={aoFechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            onClick={enviar}
            disabled={!valido || enviando}
            className="gap-2"
          >
            {enviando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnviarLocalizacaoModal;
